import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import './AdminViewingAnalytics.css';

const AdminViewingAnalytics = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewHistory, setViewHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewStats, setViewStats] = useState({
    totalViews: 0,
    uniqueItems: 0,
    categories: {},
    hourlyDistribution: {},
    topItems: []
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.items || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Хэрэглэгч авах үед алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserHistory = async (userId) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/admin/users/${userId}/history`);
      const history = response.data.items || [];
      setViewHistory(history);
      calculateStats(history);
    } catch (err) {
      setError(err.response?.data?.message || 'Түүх авах үед алдаа гарлаа');
      setViewHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (history) => {
    const stats = {
      totalViews: history.length,
      uniqueItems: new Set(history.map(h => h.itemId)).size,
      categories: {},
      hourlyDistribution: {},
      topItems: []
    };

    const itemCounts = {};

    history.forEach((item) => {
      // Count by category
      if (item.category) {
        stats.categories[item.category] = (stats.categories[item.category] || 0) + 1;
      }

      // Count by hour
      const date = new Date(item.viewedAt);
      const hour = date.getHours();
      const hourKey = `${hour}:00`;
      stats.hourlyDistribution[hourKey] = (stats.hourlyDistribution[hourKey] || 0) + 1;

      // Top items
      if (item.itemId) {
        itemCounts[item.itemId] = (itemCounts[item.itemId] || 0) + 1;
      }
    });

    // Get top 5 items
    stats.topItems = Object.entries(itemCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([itemId, count]) => ({ itemId, count }));

    setViewStats(stats);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    fetchUserHistory(user._id);
  };

  const formatHourlyData = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    return hours.map(hour => ({
      hour: `${hour}:00`,
      count: viewStats.hourlyDistribution[`${hour}:00`] || 0
    }));
  };

  return (
    <div className="admin-viewing-analytics">
      <div className="analytics-header">
        <h2>Үзэх түүх анализ</h2>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="analytics-container">
        <div className="users-sidebar">
          <h3>Хэрэглэгчүүд</h3>
          <div className="users-list">
            {loading && !selectedUser ? (
              <p>Уншиж байна...</p>
            ) : users.length === 0 ? (
              <p>Хэрэглэгч байхгүй</p>
            ) : (
              users.map((user) => (
                <button
                  key={user._id}
                  className={`user-item ${selectedUser?._id === user._id ? 'active' : ''}`}
                  onClick={() => handleSelectUser(user)}
                >
                  <div className="user-name">{user.username}</div>
                  <div className="user-email">{user.email}</div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="analytics-content">
          {selectedUser ? (
            <>
              <div className="selected-user-info">
                <h3>{selectedUser.username}</h3>
                <p>{selectedUser.email}</p>
              </div>

              {loading ? (
                <p>Анализ хийж байна...</p>
              ) : (
                <>
                  {/* Statistics Cards */}
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-label">Нийт үзсэн</div>
                      <div className="stat-number">{viewStats.totalViews}</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-label">Өөр өөр зүйл</div>
                      <div className="stat-number">{viewStats.uniqueItems}</div>
                    </div>
                  </div>

                  {/* Category Distribution */}
                  {Object.keys(viewStats.categories).length > 0 && (
                    <div className="analytics-section">
                      <h4>Ангилалын бүтээгдэхүүн</h4>
                      <div className="bar-chart">
                        {Object.entries(viewStats.categories)
                          .sort((a, b) => b[1] - a[1])
                          .map(([category, count]) => {
                            const maxCount = Math.max(...Object.values(viewStats.categories));
                            const percentage = (count / maxCount) * 100;
                            return (
                              <div key={category} className="bar-item">
                                <label>{category}</label>
                                <div className="bar-container">
                                  <div 
                                    className="bar-fill" 
                                    style={{ width: `${percentage}%` }}
                                  >
                                    <span className="bar-value">{count}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Hourly Distribution */}
                  {Object.keys(viewStats.hourlyDistribution).length > 0 && (
                    <div className="analytics-section">
                      <h4>Цаг бүрийн үзсэн зүйлс (24 цаг)</h4>
                      <div className="hourly-chart">
                        <div className="hours-grid">
                          {formatHourlyData().map(({ hour, count }) => {
                            const maxCount = Math.max(...formatHourlyData().map(h => h.count), 1);
                            const height = (count / maxCount) * 150;
                            return (
                              <div key={hour} className="hour-bar-wrapper">
                                <div 
                                  className="hour-bar" 
                                  style={{ height: `${height}px` }}
                                  title={`${hour}: ${count} үзлэг`}
                                />
                                <label>{hour}</label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Top Items */}
                  {viewStats.topItems.length > 0 && (
                    <div className="analytics-section">
                      <h4>Хамгийн их үзсэн зүйлс</h4>
                      <div className="top-items-list">
                        {viewStats.topItems.map((item, index) => (
                          <div key={item.itemId} className="top-item">
                            <span className="rank">#{index + 1}</span>
                            <span className="item-id">{item.itemId.slice(0, 12)}...</span>
                            <span className="count">{item.count} үзлэг</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Detailed History Table */}
                  <div className="analytics-section">
                    <h4>Үзэх түүхийн дэлгэрэнгүй</h4>
                    <div className="history-table-container">
                      <table className="history-table">
                        <thead>
                          <tr>
                            <th>Хүүхэлмэлийн ID</th>
                            <th>Ангилал</th>
                            <th>Үзсэн цаг</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewHistory.slice(0, 50).map((item, index) => (
                            <tr key={index}>
                              <td className="item-id">{item.itemId?.slice(0, 16)}...</td>
                              <td>{item.category || '-'}</td>
                              <td>{new Date(item.viewedAt).toLocaleString('mn-MN')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {viewHistory.length > 50 && (
                        <p className="more-items">... болон {viewHistory.length - 50} илүүдэл элемент</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="placeholder">
              <p>Хэрэглэгчийг сонгоно уу анализ үзэхийн тулд</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminViewingAnalytics;
